import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DynamicCrudPage from '../../components/DynamicCrudPage';
import { ContractDetailsModal, JobStatusViewModal } from '../../components';
import { useInvoices, useFirms, useCompanies, useJobs, usePricingProcess, usePaymentPlanTemplates, useInvoiceCodes, SCREENS } from '../../hooks';
import AddIcon from '@mui/icons-material/Add';
import { ROW_ACTIONS } from '../../constants/tableConfigurations';
import { exportInvoicesReportCSV, formatDateTR } from '../../utils';
import { useEntityAlertMap } from '../../hooks/useEntityAlertMap';

const InvoiceManagementPage = () => {
  const { t } = useTranslation();
  const { getRowSx: getInvoiceRowSx } = useEntityAlertMap('invoice');

  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [jobStatusModalOpen, setJobStatusModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const { invoices: invoicesForExport } = useInvoices();
  const { invoiceCodes } = useInvoiceCodes();

  // Payment-schedule mismatch confirmation modal.
  // Kaydet basildiginda beforeSubmit gate'i mismatch tespit ederse burayi
  // acar; kullanici "Yine de kaydet" derse pending promise resolve edilir
  // ve DynamicCrudPage devam eder.
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const [mismatchInfo, setMismatchInfo] = useState({ generalAmount: 0, months: [] });
  const mismatchResolverRef = useRef(null);
  const customFormFields = useCallback((fields, additionalData, isRootUser, t, formData) => {
    const { companies, firms, jobs, pricingProcess, invoiceCodes } = additionalData;
    const companiesList = companies?.companies || [];
    const firmsList = firms?.firms || [];
    const jobsList = jobs?.jobs || [];
    const pricingProcessList = pricingProcess?.pricingProcess || [];
    return fields
      .map(field => {
        if (field.name === 'invoice_code_ids') {
          const codes = invoiceCodes?.invoiceCodes || invoiceCodes?.data || [];
          return {
            ...field,
            itemFields: field.itemFields?.map(itemField => {
              if (itemField.name === 'invoice_code_id') {
                return {
                  ...itemField,
                  options: codes.map(c => ({ value: c.id, label: `${c.code_name}${c.product_name ? ` - ${c.product_name}` : ''}` })),
                };
              }
              if (itemField.name === 'schedules') {
                return {
                  ...itemField,
                  firmsList,
                  jobsList,
                  pricingProcessList,
                };
              }
              return itemField;
            })
          };
        }
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companiesList.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
          };
        }
        if (field.name === 'firmId') {
          return {
            ...field,
            options: firmsList.map(f => ({ value: f.id, label: f.firm_name })),
          };
        }
        if (field.name === 'firm_short_name') {
          return {
            ...field,
            type: 'select',
            options: firmsList.map(f => ({
              value: f.id,
              label: f.firm_short_name,
            })),
          };
        }
        if (field.name === 'contractId') {
          return {
            ...field,
            options: (formData) => {
              const selectedFirmId = formData.firm_established === 'yes' 
                ? formData.firmId 
                : formData.firm_short_name;
              
              if (!selectedFirmId) {
                return [];
              }

              const selectedFirm = firmsList.find(f => f.id === parseInt(selectedFirmId) || f.id === selectedFirmId);
              
              if (!selectedFirm || !selectedFirm.contracts) {
                return [];
              }

              return selectedFirm.contracts.map(contract => {
                const baseDate =
                  contract.invoice_issue_date || contract.contract_signing_date;
                return {
                  value: contract.id,
                  label: `${contract.contract_subject?.name || 'N/A'} - ${contract.scope_of_work?.name || 'N/A'} - ${
                    baseDate ? formatDateTR(baseDate) : ''
                  }`,
                  contract: contract,
                };
              });
            },
            onChange: (fieldName, value, event, formData, onChange) => {
              if (value) {
                const selectedFirmId = formData.firm_established === 'yes' 
                  ? formData.firmId 
                  : formData.firm_short_name;
                
                const selectedFirm = firmsList.find(f => f.id === parseInt(selectedFirmId) || f.id === selectedFirmId);
                
                if (selectedFirm && selectedFirm.contracts) {
                  const selectedContract = selectedFirm.contracts.find(c => c.id === parseInt(value) || c.id === value);
                if (selectedContract) {
                  onChange('contract', selectedContract);
                } 
                }
              } else {
                onChange('contract', null);
              }
              onChange(fieldName, value);
            },
          };
        }
        if (field.name === 'jobId') {
          return {
            ...field,
            options: (formData) => {
              if (!formData.contractId) {
                return [];
              }

              const contractJobs = jobsList.filter(job =>
                job.contract_id === parseInt(formData.contractId) || job.contract_id === formData.contractId
              );

              return contractJobs.map(job => {
                // TUBITAK gibi proje bazli isler icin proje numarasini
                // etikete ekle. useJobs.transformData `job_type_enum` string'ini
                // dolduruyor; iliski objesi ise `job_type.name` altinda.
                const jobTypeKey = job.job_type_enum
                  || (typeof job.job_type === 'object' ? job.job_type?.name : job.job_type)
                  || '';
                const projectNumber = job.project_number || job.projectNumber;
                const baseLabel = job.job_title || 'N/A';
                const label = jobTypeKey === 'TUBITAK' && projectNumber
                  ? `${baseLabel} - Proje No: ${projectNumber}`
                  : baseLabel;
                return {
                  value: job.id,
                  label,
                  job,
                };
              });
            },
            onChange: (fieldName, value, event, formData, onChange) => {
              if (value) {
                const contractJobs = jobsList.filter(job => 
                  job.contract_id === parseInt(formData.contractId) || job.contract_id === formData.contractId
                );
                const selectedJob = contractJobs.find(j => j.id === parseInt(value) || j.id === value);
                if (selectedJob) {
                  onChange('job', selectedJob);
                } 
              } else {
                onChange('job', null);
              }
              onChange(fieldName, value);
            },
          };
        }
        if (field.name === 'pricingProcessId') {
          return {
            ...field,
            options: (currentFormData) => {
              let filteredProcessList = pricingProcessList;
              const selectedJob = jobsList.find(j => j.id === parseInt(currentFormData.jobId) || j.id === currentFormData.jobId);

           if (
                selectedJob?.job_status !== 'ONAYLANDI' &&
                selectedJob?.job_status !== 'TAMAMLANDI'
              ) {
                filteredProcessList = pricingProcessList.filter(process =>
                  process.processName !== 'Onay Sonrası'
                );
              }
              
              return filteredProcessList.map(process => ({
                value: process.id,
                label: process.processName
              }));
            },
            onChange: (fieldName, value, event, formData, onChange) => {
              if (value) {
                const selectedPricingProcess = pricingProcessList.find(p => p.id === parseInt(value) || p.id === value);
                if (selectedPricingProcess) {
                  onChange('pricingProcess', selectedPricingProcess);
                }
              } else {
                onChange('pricingProcess', null);
              }
              onChange(fieldName, value);
            },
          };
        }
        if (field.name === 'payment_schedules') {
          return {
            ...field,
            firmsList,
            jobsList,
            pricingProcessList,
          };
        }
        if (field.name === 'pricing_method_id') {
          return {
            ...field,
            options: (formData) => {
              const selectedFirmId = formData.firm_established === 'yes'
                ? formData.firmId
                : formData.firm_short_name;

              const firm = firmsList.find(f => f.id === parseInt(selectedFirmId) || f.id === selectedFirmId);
              const contract = firm?.contracts?.find(c => c.id === parseInt(formData.contractId) || c.id === formData.contractId);
              const pricingMethod = contract?.pricing_method;

              if (pricingMethod?.id) {
                return [{ value: String(pricingMethod.id), label: pricingMethod.name || `#${pricingMethod.id}` }];
              }
              return [];
            },
          };
        }
        if (field.name === 'contract_subject_name') {
          return {
            ...field,
            options: (formData) => {
              const selectedFirmId = formData.firm_established === 'yes'
                ? formData.firmId
                : formData.firm_short_name;
              const firm = firmsList.find(f => f.id === parseInt(selectedFirmId) || f.id === selectedFirmId);
              const contract = firm?.contracts?.find(c => c.id === parseInt(formData.contractId) || c.id === formData.contractId) || formData.contract;
              const subjectName = contract?.contract_subject?.name;
              const isProjectBased = contract?.contract_subject?.is_project_based;
              const suffix = typeof isProjectBased === 'boolean' 
                ? ` - ${isProjectBased ? 'Proje bazlı' : 'Proje bazlı değil'}`
                : '';
              const label = subjectName ? `${subjectName}${suffix}` : 'N/A';
              return subjectName ? [{ value: subjectName, label }] : [];
            },
          };
        }
        if (field.name === 'invoice_code_id') {
          const codes = invoiceCodes?.invoiceCodes || invoiceCodes?.data || [];
          return {
            ...field,
            options: codes.map(c => ({ value: c.id, label: `${c.code_name}${c.product_name ? ` - ${c.product_name}` : ''}` })),
          };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, []);

  const transformEditData = useCallback(invoiceData => {
    if (!invoiceData) return {};

    const formatDateForForm = dateString => {
      if (!dateString) return null;
      if (dateString instanceof Date) return dateString;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    };
    return {
      ...invoiceData,
      companyId: invoiceData.company_id,
      firm_established: invoiceData.firm_established ? 'yes' : 'no',
      firmId: invoiceData.firm_id,
      firm_short_name: invoiceData.firm_id,
      contractId: invoiceData.contract_id,
      jobId: invoiceData.job_id,
      pricingProcessId: invoiceData.pricing_process_id,
      invoice_name: invoiceData.invoice_name || '',
      split_invoice: invoiceData.split_invoice || false,
      custom_date_range: invoiceData.custom_date_range || false,
      date_range_start: formatDateForForm(invoiceData.date_range_start),
      date_range_end: formatDateForForm(invoiceData.date_range_end),
      payment_plan_template_id: invoiceData.payment_plan_template_id,
      payment_schedules: invoiceData.payment_schedules || [],
      total_unit_price: invoiceData.total_unit_price || 0,
      change_pricing_method: invoiceData.change_pricing_method || false,
      new_pricing_type: invoiceData.new_pricing_type || '',
      new_start_date: formatDateForForm(invoiceData.new_start_date),
      pricing_method_id: invoiceData.contract?.pricing_method_id || invoiceData.contract?.pricing_method?.id || null,
      pricing_method: invoiceData.contract?.pricing_method || null,
      invoice_code_ids: invoiceData.invoice_code_ids || [],
    };
  }, []);

  const customRowActions = useCallback(
    row => {
      return ROW_ACTIONS.invoices(row);
    },
    []
  );

  // FILTER_CONFIGS.invoices icindeki `job_type` filtresi icin option
  // etiketlerini burada cevir. DynamicCrudPage sadece `status` filtresi
  // icin labelKey -> label donusumu yapiyor; enum etiketlerini kaybetmemek
  // adina custom yolla map'liyoruz. Ayrica default'un `showIfRoot`
  // guard'ini korumak icin filter zincirini de burada uyguluyoruz.
  const customFilterConfigs = useCallback((filters, additionalData, isRootUser, t) => {
    // Ucretlendirme sureci options'i runtime doldur — sayfa datasindan degil,
    // tum listeden. additionalData.pricingProcess.pricingProcess yapisi
    // usePricingProcess hook'unun return sekli.
    const pricingProcessList = additionalData?.pricingProcess?.pricingProcess || [];
    return filters
      .filter(f => (f.showIfRoot ? isRootUser : true))
      .map(f => {
        if (f.field === 'pricing_process_id') {
          return {
            ...f,
            options: pricingProcessList.map(p => ({
              value: p.id,
              label: p.name,
            })),
          };
        }
        if (Array.isArray(f.options) && f.options.some(opt => opt.labelKey)) {
          return {
            ...f,
            options: f.options.map(opt => ({
              value: opt.value,
              label: opt.labelKey ? t(opt.labelKey) : opt.label,
            })),
          };
        }
        return f;
      });
  }, []);

  const handleViewContract = useCallback((row) => {
    if (row.contract) {
      setSelectedContract(row.contract);
      setContractModalOpen(true);
    }
  }, []);

  const handleCloseContractModal = useCallback(() => {
    setContractModalOpen(false);
    setSelectedContract(null);
  }, []);

  const handleViewJobStatus = useCallback((row) => {
    if (row.job) {
      setSelectedJob(row.job);
      setJobStatusModalOpen(true);
    }
  }, []);

  const handleCloseJobStatusModal = useCallback(() => {
    setJobStatusModalOpen(false);
    setSelectedJob(null);
  }, []);

  const handleRowAction = useCallback((actionType, row) => {
    switch (actionType) {
      case 'viewContract':
        handleViewContract(row);
        break;
      case 'viewJobStatus':
        handleViewJobStatus(row);
        break;
      default:
        return false;
    }
  }, [handleViewContract, handleViewJobStatus]);

  // Duzenli faturada `payment_schedules` + `total_unit_price` iceriyor.
  // Split fatura durumunda ise mismatch her `invoice_code_ids[i].schedules`
  // icin `invoice_code_ids[i].amount` ile karsilastirilir.
  const collectMismatches = useCallback((formData) => {
    const results = [];
    const format = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });

    const pushMonthsForGroup = (schedules, generalAmount, groupLabel) => {
      if (!Array.isArray(schedules) || !(generalAmount > 0)) return;
      schedules
        .filter(s => !s.is_transfer && s.is_active)
        .forEach(s => {
          const amount = parseFloat(s.amount) || 0;
          if (Math.abs(amount - generalAmount) > 0.01) {
            const monthLabel = s.yearMonth || (s.monthName && s.year ? `${s.year} ${s.monthName}` : '');
            results.push({
              group: groupLabel,
              month: monthLabel,
              amount: format(amount),
              expected: format(generalAmount),
            });
          }
        });
    };

    if (formData?.split_invoice === true && Array.isArray(formData?.invoice_code_ids)) {
      formData.invoice_code_ids.forEach((item, idx) => {
        const groupLabel = item?.invoice_code_id
          ? `#${idx + 1}`
          : `#${idx + 1}`;
        pushMonthsForGroup(item?.schedules, parseFloat(item?.amount) || 0, groupLabel);
      });
    } else {
      const generalAmount = parseFloat(formData?.total_unit_price) || 0;
      pushMonthsForGroup(formData?.payment_schedules, generalAmount, null);
    }

    return { months: results };
  }, []);

  const handleBeforeSubmit = useCallback((formData, isEditMode) => {
    // Uyari sadece edit modunda anlamli — PaymentScheduleField'in mevcut
    // `hasAmountMismatch` guard'iyla ayni davranis (isEditMode true iken).
    if (!isEditMode) return true;

    const { months } = collectMismatches(formData);
    if (months.length === 0) return true;

    const generalAmount = formData?.split_invoice === true
      ? null
      : parseFloat(formData?.total_unit_price) || 0;

    setMismatchInfo({ generalAmount: generalAmount || 0, months });
    setMismatchDialogOpen(true);
    return new Promise((resolve) => {
      mismatchResolverRef.current = resolve;
    });
  }, [collectMismatches]);

  const handleMismatchCancel = useCallback(() => {
    setMismatchDialogOpen(false);
    if (mismatchResolverRef.current) {
      mismatchResolverRef.current(false);
      mismatchResolverRef.current = null;
    }
  }, []);

  const handleMismatchProceed = useCallback(() => {
    setMismatchDialogOpen(false);
    if (mismatchResolverRef.current) {
      mismatchResolverRef.current(true);
      mismatchResolverRef.current = null;
    }
  }, []);

  return (
    <>
      <DynamicCrudPage
        entityType="invoices"
        screen={SCREENS.INVOICE_MANAGEMENT}
        commentEntityType="invoice"
        hook={useInvoices}
        additionalHooks={{
          firms: useFirms,
          companies: useCompanies,
          jobs: useJobs,
          pricingProcess: usePricingProcess,
          paymentPlanTemplates: usePaymentPlanTemplates,
          invoiceCodes: useInvoiceCodes,
        }}
        addButtonTextKey="table.actions.addInvoice"
        addButtonIcon={<AddIcon />}
        entityTypeForDataTable="invoice"
        customFormFields={customFormFields}
        customFilterConfigs={customFilterConfigs}
        customTransformEditData={transformEditData}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        beforeSubmit={handleBeforeSubmit}
        showAddButton={true}
        exportButton={true}
        exportLabel={'invoices.actions.exportCsv'}
        onExport={() => exportInvoicesReportCSV(invoicesForExport || [], (invoiceCodes || []))}
        getRowSx={getInvoiceRowSx}
      >
      </DynamicCrudPage>
      
      <ContractDetailsModal
        open={contractModalOpen}
        onClose={handleCloseContractModal}
        contract={selectedContract}
      />
      
      <JobStatusViewModal
        open={jobStatusModalOpen}
        onClose={handleCloseJobStatusModal}
        job={selectedJob}
      />

      <Dialog
        open={mismatchDialogOpen}
        onClose={handleMismatchCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
            {t('invoices.form.paymentSchedule.mismatchDialog.title')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('invoices.form.paymentSchedule.mismatchDialog.message')}
          </Typography>
          {mismatchInfo.generalAmount > 0 && (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('invoices.form.paymentSchedule.mismatchDialog.expected')}: ₺
              {Number(mismatchInfo.generalAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {mismatchInfo.months.map((m, i) => (
              <Chip
                key={i}
                onClick={() => {}}
                color="warning"
                variant="outlined"
                label={`${m.group ? `${m.group} · ` : ''}${m.month}: ₺${m.amount}`}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMismatchCancel} color="inherit">
            {t('invoices.form.paymentSchedule.mismatchDialog.cancel')}
          </Button>
          <Button onClick={handleMismatchProceed} variant="contained" color="warning">
            {t('invoices.form.paymentSchedule.mismatchDialog.proceed')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceManagementPage;
